/**
 * Partner offers, for staff (REQ-025 AC-001, AC-007).
 *
 * Insurers' offers to travellers: created, edited and paused here, never by
 * the insurer in phase 1. Each row shows what the offer has produced — clicks,
 * contracts the insurer reported, and the commission those make under the
 * offer's fee model — computed on the server from the records, never typed.
 *
 * The conversion import takes the click ids an insurer reports, pasted or
 * from a file, and says back which ones were not this insurer's clicks or had
 * been reported before, so the report can be reconciled with theirs.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Tag, Loader2, Pause, Pencil, Play, Plus, Upload } from "lucide-react";
import { useState } from "react";
import { useLocaleFormat } from "@/hooks/useLocaleFormat";
import apiClient from "@/services/api/apiClient";

type FeeModel = "per_contract" | "flat_per_click";
type Category = "travel_health" | "repatriation" | "cancellation" | "other";
type Language = "en" | "de" | "fr" | "es" | "pt";

interface Offer {
  id: string;
  partnerId: string;
  partnerName: string;
  productName: string;
  category: Category;
  description: Partial<Record<Language, string>>;
  url: string;
  residenceCountries: string[];
  destinationCountries: string[];
  startsOn: string;
  endsOn: string | null;
  status: "active" | "paused";
  feeModel: FeeModel;
  feeAmountMinor: number;
  currency: string;
  clicks: number;
  conversions: number;
  commissionMinor: number;
}

interface Partner {
  id: string;
  organisation: string;
  type: string;
  stage: string;
}

interface ImportResult {
  recorded: number;
  unknown: string[];
  duplicate: string[];
  notPerContract: string[];
}

const LANGUAGES: Language[] = ["en", "de", "fr", "es", "pt"];

const CATEGORY_LABEL: Record<Category, string> = {
  travel_health: "Travel health",
  repatriation: "Repatriation",
  cancellation: "Cancellation",
  other: "Other",
};

const FEE_LABEL: Record<FeeModel, string> = {
  per_contract: "Per contract",
  flat_per_click: "Flat per click",
};

const money = (minor: number, currency: string, locale: string) => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toFixed(2)} ${currency}`;
  }
};

const said = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: { message?: string } } } })
    ?.response?.data?.error?.message ?? fallback;

const codes = (value: FormDataEntryValue | null) =>
  String(value ?? "")
    .split(/[,\s]+/)
    .map((code) => code.trim().toUpperCase())
    .filter(Boolean);

const dayOf = (iso: string | null) => (iso ? iso.slice(0, 10) : "");

const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm";

function OfferForm({
  offer,
  partners,
  onDone,
}: {
  offer: Offer | null;
  partners: Partner[];
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [refusal, setRefusal] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (form: FormData) => {
      const description: Partial<Record<Language, string>> = {};
      for (const language of LANGUAGES) {
        description[language] = String(
          form.get(`description_${language}`) ?? "",
        );
      }
      const body = {
        ...(offer ? {} : { partnerId: String(form.get("partnerId")) }),
        productName: String(form.get("productName") ?? "").trim(),
        category: String(form.get("category")),
        description,
        url: String(form.get("url") ?? "").trim(),
        residenceCountries: codes(form.get("residenceCountries")),
        destinationCountries: codes(form.get("destinationCountries")),
        startsOn: String(form.get("startsOn") ?? ""),
        endsOn: String(form.get("endsOn") ?? "") || null,
        status: String(form.get("status")),
        feeModel: String(form.get("feeModel")),
        feeAmountMinor: Math.round(Number(form.get("feeAmount") ?? 0) * 100),
        currency: String(form.get("currency") ?? "").trim(),
      };
      if (offer) {
        await apiClient.patch(`/staff/marketplace/offers/${offer.id}`, body);
      } else {
        await apiClient.post("/staff/marketplace/offers", body);
      }
    },
    onSuccess: () => {
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["marketplace-offers"] });
      onDone();
    },
    onError: (error: unknown) =>
      setRefusal(said(error, "The offer could not be saved.")),
  });

  const sellers = partners.filter(
    (partner) => partner.type === "insurer" && partner.stage !== "ended",
  );

  return (
    <form
      className="grid gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate(new FormData(event.currentTarget));
      }}
    >
      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Insurer</span>
        {offer ? (
          <input value={offer.partnerName} disabled className={inputCls} />
        ) : (
          <select name="partnerId" required className={inputCls}>
            {sellers.map((partner) => (
              <option key={partner.id} value={partner.id}>
                {partner.organisation}
              </option>
            ))}
          </select>
        )}
        {!offer && sellers.length === 0 && (
          <span className="mt-1 block text-xs text-amber-700">
            No insurer partner without an ended contract. Add one under Partners
            first.
          </span>
        )}
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">
          Product name
        </span>
        <input
          name="productName"
          required
          defaultValue={offer?.productName}
          className={inputCls}
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Category</span>
        <select
          name="category"
          defaultValue={offer?.category ?? "travel_health"}
          className={inputCls}
        >
          {Object.entries(CATEGORY_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">
          Insurer's link (https)
        </span>
        <input
          name="url"
          type="url"
          required
          defaultValue={offer?.url}
          placeholder="https://"
          className={inputCls}
        />
      </label>

      {LANGUAGES.map((language) => (
        <label key={language} className="text-sm sm:col-span-2">
          <span className="mb-1 block font-medium text-gray-700">
            Insurer's description ({language.toUpperCase()})
          </span>
          <textarea
            name={`description_${language}`}
            rows={2}
            defaultValue={offer?.description[language] ?? ""}
            className={inputCls}
          />
        </label>
      ))}

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">
          Countries of residence it may be sold to
        </span>
        <input
          name="residenceCountries"
          required
          defaultValue={offer?.residenceCountries.join(", ")}
          placeholder="DE, AT, CH"
          className={inputCls}
        />
        <span className="mt-1 block text-xs text-gray-500">
          ISO codes. An offer sold to US residents must be paid per click.
        </span>
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">
          Destinations covered
        </span>
        <input
          name="destinationCountries"
          defaultValue={offer?.destinationCountries.join(", ")}
          placeholder="Empty means worldwide"
          className={inputCls}
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Starts</span>
        <input
          name="startsOn"
          type="date"
          required
          defaultValue={dayOf(offer?.startsOn ?? null)}
          className={inputCls}
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Ends</span>
        <input
          name="endsOn"
          type="date"
          defaultValue={dayOf(offer?.endsOn ?? null)}
          className={inputCls}
        />
      </label>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Fee model</span>
        <select
          name="feeModel"
          defaultValue={offer?.feeModel ?? "per_contract"}
          className={inputCls}
        >
          {Object.entries(FEE_LABEL).map(([id, label]) => (
            <option key={id} value={id}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Fee</span>
          <input
            name="feeAmount"
            type="number"
            min={0}
            step="0.01"
            required
            defaultValue={offer ? offer.feeAmountMinor / 100 : undefined}
            className={inputCls}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block font-medium text-gray-700">Currency</span>
          <input
            name="currency"
            required
            maxLength={3}
            defaultValue={offer?.currency ?? "EUR"}
            className={inputCls}
          />
        </label>
      </div>

      <label className="text-sm">
        <span className="mb-1 block font-medium text-gray-700">Status</span>
        <select
          name="status"
          defaultValue={offer?.status ?? "paused"}
          className={inputCls}
        >
          <option value="paused">Paused</option>
          <option value="active">Active</option>
        </select>
      </label>

      {refusal && (
        <p className="text-sm text-red-700 sm:col-span-2">{refusal}</p>
      )}

      <div className="flex gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
        >
          {save.isPending ? "Saving…" : "Save offer"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function ConversionImport({ partners }: { partners: Partner[] }) {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [result, setResult] = useState<ImportResult | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const insurers = partners.filter((partner) => partner.type === "insurer");

  const run = useMutation({
    mutationFn: async (form: FormData) => {
      const response = await apiClient.post<{ data: ImportResult }>(
        "/staff/marketplace/conversions",
        {
          partnerId: String(form.get("partnerId")),
          reportedOn: String(form.get("reportedOn") ?? "") || undefined,
          clickIds: text,
        },
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setRefusal(null);
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ["marketplace-offers"] });
    },
    onError: (error: unknown) => {
      setResult(null);
      setRefusal(said(error, "The report could not be imported."));
    },
  });

  return (
    <form
      className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
      onSubmit={(event) => {
        event.preventDefault();
        run.mutate(new FormData(event.currentTarget));
      }}
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
        <Upload className="h-4 w-4 text-sky-600" /> Import an insurer's
        conversion report
      </h2>
      <p className="text-xs text-gray-500">
        The click ids of contracts the insurer reports as concluded, one per
        line or comma-separated. Ids that are not this insurer's clicks, or were
        reported before, are listed back and not recorded.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <select name="partnerId" required className={inputCls}>
          {insurers.map((partner) => (
            <option key={partner.id} value={partner.id}>
              {partner.organisation}
            </option>
          ))}
        </select>
        <input
          name="reportedOn"
          type="date"
          className={inputCls}
          aria-label="Reported on"
        />
        <input
          type="file"
          accept=".csv,.txt,text/plain,text/csv"
          className="text-sm"
          aria-label="Report file"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            if (file) setText(await file.text());
          }}
        />
      </div>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={4}
        placeholder="Click ids"
        className={`${inputCls} font-mono`}
      />
      <button
        type="submit"
        disabled={run.isPending || !text.trim()}
        className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
      >
        {run.isPending ? "Importing…" : "Import"}
      </button>
      {refusal && <p className="text-sm text-red-700">{refusal}</p>}
      {result && (
        <div className="space-y-1 text-sm text-gray-700">
          <p>{result.recorded} recorded.</p>
          {result.unknown.length > 0 && (
            <p className="text-red-700">
              Unknown ({result.unknown.length}): {result.unknown.join(", ")}
            </p>
          )}
          {result.duplicate.length > 0 && (
            <p className="text-amber-700">
              Already reported ({result.duplicate.length}):{" "}
              {result.duplicate.join(", ")}
            </p>
          )}
          {result.notPerContract.length > 0 && (
            <p className="text-amber-700">
              On offers paid per click, not recorded (
              {result.notPerContract.length}):{" "}
              {result.notPerContract.join(", ")}
            </p>
          )}
        </div>
      )}
    </form>
  );
}

export default function PartnerOffersAdminPage() {
  const queryClient = useQueryClient();
  const fmt = useLocaleFormat();
  const [editing, setEditing] = useState<Offer | "new" | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const offers = useQuery({
    queryKey: ["marketplace-offers"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: { offers: Offer[] } }>(
        "/staff/marketplace/offers",
      );
      return response.data.data.offers;
    },
  });

  const partners = useQuery({
    queryKey: ["partners", "insurers"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: { partners: Partner[] } }>(
        "/staff/partners?type=insurer",
      );
      return response.data.data.partners;
    },
  });

  const toggle = useMutation({
    mutationFn: async (offer: Offer) =>
      apiClient.patch(`/staff/marketplace/offers/${offer.id}`, {
        status: offer.status === "active" ? "paused" : "active",
      }),
    onSuccess: () => {
      setRowError(null);
      void queryClient.invalidateQueries({ queryKey: ["marketplace-offers"] });
    },
    onError: (error: unknown) =>
      setRowError(said(error, "The offer could not be changed.")),
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Tag className="h-5 w-5 text-sky-600" /> Partner offers
        </h1>
        <button
          onClick={() => setEditing(editing === "new" ? null : "new")}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
        >
          <Plus className="h-4 w-4" /> Add an offer
        </button>
      </div>

      <p className="text-sm text-gray-600">
        Offers are shown to travellers by trip destination, dates and country of
        residence only, in alphabetical order, with no ranking. A new offer
        starts paused.
      </p>

      {editing && (
        <OfferForm
          key={editing === "new" ? "new" : editing.id}
          offer={editing === "new" ? null : editing}
          partners={partners.data ?? []}
          onDone={() => setEditing(null)}
        />
      )}

      {rowError && <p className="text-sm text-red-700">{rowError}</p>}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {offers.isLoading ? (
          <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading offers…
          </div>
        ) : offers.isError ? (
          <div className="p-6 text-sm text-red-700">
            Offers could not be read.
          </div>
        ) : !offers.data?.length ? (
          <div className="p-8 text-sm text-gray-500">No offers yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Offer</th>
                  <th className="px-4 py-2 font-medium">Where</th>
                  <th className="px-4 py-2 font-medium">Runs</th>
                  <th className="px-4 py-2 font-medium">Fee</th>
                  <th className="px-4 py-2 font-medium">Clicks</th>
                  <th className="px-4 py-2 font-medium">Contracts</th>
                  <th className="px-4 py-2 font-medium">Commission</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {offers.data.map((offer) => (
                  <tr key={offer.id} className="align-top">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">
                        {offer.productName}
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">
                        {offer.partnerName} · {CATEGORY_LABEL[offer.category]}
                      </span>
                      <span
                        className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                          offer.status === "active"
                            ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                            : "bg-gray-100 text-gray-600 ring-gray-400/20"
                        }`}
                      >
                        {offer.status === "active" ? "Active" : "Paused"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      <span className="block">
                        Residents: {offer.residenceCountries.join(", ")}
                      </span>
                      <span className="block">
                        To:{" "}
                        {offer.destinationCountries.length
                          ? offer.destinationCountries.join(", ")
                          : "worldwide"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {dayOf(offer.startsOn)} – {dayOf(offer.endsOn) || "open"}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {money(offer.feeAmountMinor, offer.currency, fmt.locale)}
                      <span className="block">{FEE_LABEL[offer.feeModel]}</span>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {offer.clicks}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {offer.feeModel === "per_contract"
                        ? offer.conversions
                        : "—"}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-gray-900">
                      {money(offer.commissionMinor, offer.currency, fmt.locale)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          title="Edit"
                          aria-label={`Edit ${offer.productName}`}
                          onClick={() => setEditing(offer)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title={
                            offer.status === "active" ? "Pause" : "Activate"
                          }
                          aria-label={`${offer.status === "active" ? "Pause" : "Activate"} ${offer.productName}`}
                          disabled={toggle.isPending}
                          onClick={() => toggle.mutate(offer)}
                          className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                        >
                          {offer.status === "active" ? (
                            <Pause className="h-4 w-4" />
                          ) : (
                            <Play className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConversionImport partners={partners.data ?? []} />
    </div>
  );
}
